import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { mutations } from 'RestAPIs/rapini';
import { showSnackbar } from 'components/shared';

import {
  BulkEditParticipantsActionAppointmentRequest,
  EditAppointmentTypeResponse,
  EventResponse,
  PlanningEntityIdentifier
} from 'generated/index';

type UseBulkEditParticipantsParams = {
  onSuccess: () => void;
};

export const useBulkEditParticipants = ({
  onSuccess
}: UseBulkEditParticipantsParams) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const { mutate, isLoading } =
    mutations.useAppointmentBulkEditParticipantsAction({
      onSuccess: (data) => {
        queryClient.invalidateQueries('appointmentsOverview');
        onSuccess();

        if (data?.success) {
          showSnackbar({
            severity: 'success',
            message: t('appointmentsOverview.editParticipants.success')
          });
        } else {
          showSnackbar({
            severity: 'error',
            message: t('appointmentsOverview.editParticipants.failure')
          });
        }
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('appointmentsOverview.editParticipants.failure')
        });
      }
    });

  const editParticipants = (
    appointments: EventResponse[],
    addParticipants: PlanningEntityIdentifier[],
    removeParticipants: PlanningEntityIdentifier[],
    editAppointmentType: EditAppointmentTypeResponse
  ) => {
    const payload: BulkEditParticipantsActionAppointmentRequest = {
      appointmentOccurrenceIdentifiers: appointments.map((appointment) => ({
        entityId: appointment.id,
        originalOccurrenceStart: appointment.start,
        schedulingLabel: 'MRP'
      })),
      addParticipants,
      removeParticipants,
      editAppointmentType
    };

    mutate(payload);
  };

  return { editParticipants, isLoading };
};
